// © 2026 AttainIP Engineering
/**
 * Route guard for the Workspace — the case browser and the two-pane case
 * surface (RFC-0206 Surface 1).
 *
 * Gated on BOTH the enterprise flavour and the per-environment runtime flag —
 * see `isWorkspaceEnabled()`. The Workspace is enterprise-only (RFC-0206 D6)
 * AND off by default in every environment, so production is unaffected until
 * someone enables it there explicitly.
 *
 * The Workspace is ENTERPRISE ONLY (RFC-0206 D6, provisional). A
 * self-service account is one person working through their own analyses, and
 * the dashboard already lists all of them; grouping that set by case is a
 * navigation layer over something small enough not to need one. A firm is the
 * opposite — many people, many matters, and the case is the unit colleagues
 * hand to each other.
 *
 * ⚠️ THE GUARD IS THE ENFORCEMENT, NOT THE NAV. Self-service also has no left
 * nav, so nothing *links* here — but "no link" is not "no route": a bookmark,
 * a shared URL, or a stale tab would otherwise render an enterprise surface
 * inside a self-service build. Absence from navigation is a UX decision;
 * this is the one that actually holds.
 *
 * Redirects to the dashboard rather than a 404: for a self-service user this
 * URL is not wrong, it simply does not exist in their product, and the
 * dashboard is where the same work lives.
 *
 * Nest inside <RequireAuth>. Mirrors RequireOrgAdmin's shape deliberately —
 * flavour gating should look the same wherever it appears.
 */
import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { isWorkspaceEnabled } from '../../config/features';
import { useAuthStore } from '../../store/auth';

interface RequireWorkspaceProps {
  children: ReactNode;
}

export function RequireWorkspace({ children }: RequireWorkspaceProps) {
  // Read from the store, not a fetch — the org override arrived with the
  // session, so this stays synchronous and the guard renders in one pass.
  const user = useAuthStore((s) => s.user);

  if (!isWorkspaceEnabled(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
